import React, { useEffect, useRef, useState } from "react";
import { Loader2, CalendarClock } from "lucide-react";
import { publicApi } from "../../lib/api";
import { loadCalendly } from "../BookCallButton";

// Inline Calendly for the close of the journey. The booking URL is still fetched
// through the human-check endpoint, so it never sits in the page source.
export const DiscoveryBooking = ({ prefill, heading, intro }) => {
  const box = useRef(null);
  const [state, setState] = useState("loading");
  const started = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // No elapsed_ms: there is no click to time here. The visitor has just
        // completed the whole pricing journey, and sending a 1ms "reaction time"
        // tripped the bot guard, so the calendar never loaded.
        const { url } = await publicApi.bookingLink({ website: "" });
        if (!/calendly\.com/i.test(url)) {
          if (!cancelled) { window.open(url, "_blank", "noopener,noreferrer"); setState("external"); }
          return;
        }
        const Calendly = await loadCalendly();
        if (cancelled || !box.current) return;
        Calendly.initInlineWidget({
          url,
          parentElement: box.current,
          prefill: { name: prefill?.name || "", email: prefill?.email || "" },
        });
        setState("ready");
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="package-booking">
      <div className="text-center">
        <CalendarClock className="text-orange mx-auto" size={28} />
        <h3 className="font-display font-extrabold text-navy text-2xl md:text-3xl mt-3">{heading || "Book your Discovery Call"}</h3>
        <p className="text-sm text-slate-600 mt-3 max-w-xl mx-auto leading-relaxed">
          {intro || "Pick a time that suits you. We'll go through what you've built and confirm the figure."}
        </p>
      </div>
      {state === "loading" && (
        <div className="py-16 text-center" data-testid="package-booking-loading">
          <Loader2 size={24} className="animate-spin text-orange mx-auto" />
          <p className="text-sm text-slate-500 mt-3">Opening the calendar…</p>
        </div>
      )}
      {state === "error" && (
        <p className="text-center text-sm text-slate-600 mt-8" data-testid="package-booking-error">
          We couldn't open the calendar just now. Give us a ring on <a href="tel:+441827780550" className="text-orange font-semibold">01827 780550</a> and
          we'll get you booked in.
        </p>
      )}
      {state === "external" && (
        <p className="text-center text-sm text-slate-600 mt-8">The booking page has opened in a new tab.</p>
      )}
      {/* Calendly's iframe is height:100%, which doesn't resolve against a
          min-height — the container needs a real height or the calendar
          collapses to the 150px iframe default. */}
      <div ref={box} className={`mt-6 ${state === "ready" ? "h-[940px] sm:h-[760px] lg:h-[700px]" : ""}`} data-testid="package-booking-widget" />
    </div>
  );
};

export default DiscoveryBooking;
